import axios from 'axios';
import { getSupplierIdByNameSync, getSupplierNameByIdSync } from '../data/suppliers';
import { SupplierUserTypes } from '../types/userTypes';

// Function to get API base URL based on selected environment
const getApiBaseUrl = () => {
  const environment = localStorage.getItem('selectedEnvironment') || 'testing';
  
  let baseUrl;
  switch (environment) {
    case 'localhost':
    case 'development':
      baseUrl = 'http://localhost:8008';
      break;
    case 'testing':
      baseUrl = 'https://be-test.dev.qaraservices.net';
      break;
    case 'staging':
      baseUrl = 'https://hse-staging.dev.qaraservices.net';
      break;
    case 'production':
      baseUrl = 'https://sweedypro.dev.qaraservicesproduction.com';
      break;
    default:
      baseUrl = 'http://localhost:8008';
  }
  
  return baseUrl;
};

// API credentials for backend authentication
const getCredentials = () => {
  const environment = localStorage.getItem('selectedEnvironment') || 'testing';
  
  // Environment-specific default credentials
  let defaultUsername, defaultPassword;
  
  switch (environment) {
    case 'localhost':
    case 'development':
      // Local development - no credentials needed for Google Auth
      defaultUsername = 'qara-sheets';
      defaultPassword = 'qara2024';
      break;
    case 'production':
      // Production might require different credentials
      defaultUsername = 'qara-sheets'; // You may need to update this
      defaultPassword = 'qara2024';         // You may need to update this
      break;
    case 'staging':
      defaultUsername = 'qara-sheets';
      defaultPassword = 'qara2024';
      break;
    case 'testing':
    default:
      defaultUsername = 'qara-sheets';
      defaultPassword = 'qara2024';
      break;
  }
  
  // Try to get dynamic credentials first, fall back to environment-specific ones
  const username = localStorage.getItem('api_username') || defaultUsername;
  const password = localStorage.getItem('api_password') || defaultPassword;
  const supplierName = localStorage.getItem('excel_supplier_name') || 'Saint-Gobain';
  const supplierId = getSupplierIdByNameSync(supplierName) || '6683aa63d71fa531dd60b807';
  
  return { username, password, supplierName, supplierId };
};

const apiClient = axios.create({
  timeout: 120000, // 2 minutes timeout for debugging
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json, */*',
  }
});
// Request interceptor to add auth headers and set dynamic base URL
apiClient.interceptors.request.use(
  (config) => {
    // Set dynamic base URL based on selected environment
    config.baseURL = getApiBaseUrl();
    
    const { username, password, supplierId } = getCredentials();
    
    // Add fixed API credentials to all requests - matching Google Apps Script format
    config.headers['Username'] = username;
    config.headers['password'] = password;
    
    // Add supplier parameter to requests based on endpoint type
    // Skip adding supplier param for endpoints that already have it in the URL path or params
    const isUserTypesEndpoint = config.url?.includes('/user-types/findUserTypesBySupplier/');
    const isResetLogsEndpoint = config.url?.includes('/reset-logs');
    const isJoiningRequestsEndpoint = config.url?.includes('/joining-request/data/sheet/joining-request');
    const isWalletEndpoint = config.url?.includes('/wallet-transactions/') && 
                            (config.url.includes('/WalletTransactionSheet') || 
                             config.url.includes('/deliverCash') || 
                             config.url.includes('/deliverTransfer'));
    const isMultyOperationsEndpoint = config.url?.includes('/wallet-transactions/multy-operations');
    
    if (!config.params?.supplierId && !config.params?.supplier && !isUserTypesEndpoint && !isResetLogsEndpoint && !isJoiningRequestsEndpoint) {
      if (isWalletEndpoint) {
        // Use supplier name for wallet endpoints (except multy-operations and WelcomeBonus)
        const isWelcomeBonusEndpoint = config.url?.includes('/WelcomeBonusForSheet');
        if (isWelcomeBonusEndpoint) {
          // Use supplier ID for Welcome Bonus endpoint
          config.params = { ...config.params, supplierId: supplierId };
        } else {
          const { supplierName } = getCredentials();
          config.params = { ...config.params, supplier: supplierName };
        }
      } else if (isMultyOperationsEndpoint) {
        // Use supplier ID for multy-operations endpoint
        console.log('Multy operations endpoint detected, using supplier ID:', supplierId);
        config.params = { ...config.params, supplier: supplierId };
      } else if (supplierId) {
        // Use supplier ID for other endpoints
        config.params = { ...config.params, supplier: supplierId };
      }
    }
    
    // Ensure Accept header is always set
    if (!config.headers['Accept']) {
      config.headers['Accept'] = 'application/json, */*';
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const environment = localStorage.getItem('selectedEnvironment') || 'testing';
    
    if (error.response?.status === 401 || error.response?.status === 403) {
      // For production, don't auto-redirect as credentials might need to be updated
      if (environment !== 'production') {
        localStorage.removeItem('excel_supplier_name');
        window.location.href = '/login';
      }
    }
    
    return Promise.reject(error);
  }
);

// Simple cache for user types to prevent multiple rapid API calls
const userTypesCache = new Map<string, { data: any; timestamp: number }>();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Request deduplication to prevent multiple simultaneous calls
const pendingRequests = new Map<string, Promise<any>>();

export const apiService = {
  // Get current environment
  getCurrentEnvironment: () => {
    return localStorage.getItem('selectedEnvironment') || 'testing';
  },

  // Get current API base URL
  getCurrentApiUrl: () => {
    return getApiBaseUrl();
  },

  // User session management (Google OAuth only)
  setSupplier: (supplierId: string) => {
    const supplierName = getSupplierNameByIdSync(supplierId);
    if (supplierName) {
      localStorage.setItem('excel_supplier_name', supplierName);
    }
  },

  // Set API credentials (for testing purposes)
  setCredentials: (username: string, password: string) => {
    // Store credentials in localStorage for dynamic access
    localStorage.setItem('api_username', username);
    localStorage.setItem('api_password', password);
  },

  // Get current credentials being used
  getCurrentCredentials: () => {
    const creds = getCredentials();
    return {
      username: creds.username,
      password: creds.password ? '[SET]' : '[MISSING]',
      supplier: creds.supplierName,
      environment: localStorage.getItem('selectedEnvironment') || 'testing'
    };
  },

  clearSession: () => {
    localStorage.removeItem('excel_supplier_name');
    localStorage.removeItem('api_username');
    localStorage.removeItem('api_password');
    // Clear user types cache when session is cleared
    userTypesCache.clear();
  },

  // Clear user types cache manually if needed
  clearUserTypesCache: () => {
    userTypesCache.clear();
    pendingRequests.clear();
    console.log('User types cache and pending requests cleared');
  },

  // Test connectivity - simplified for production compatibility
  healthCheck: async () => {
    const environment = localStorage.getItem('selectedEnvironment') || 'testing';
    const baseUrl = getApiBaseUrl();
    
    // For production, skip the health check and return a mock success
    // since the production API might not support health checks or our authentication method
    if (environment === 'production') {
      return { 
        status: 200, 
        data: { message: 'Production health check skipped - connection assumed valid' },
        config: {},
        headers: {},
        statusText: 'OK'
      };
    }
    
    try {
      // For testing/staging, try health endpoint
      const response = await apiClient.get('/health', {
        headers: {
          'Accept': 'application/json'
        }
      });
      return response;
    } catch (error: any) {
      // For non-production, try a fallback endpoint
      try {
        const fallbackResponse = await apiClient.get('/users/production/feeds', {
          params: { limit: 1, allowPagination: true },
          headers: {
            'Accept': 'application/json'
          }
        });
        return fallbackResponse;
      } catch (fallbackError: any) {
        throw error; // Throw the original error
      }
    }
  },

  // Helper function for Excel endpoints
  callExcelEndpoint: (endpoint: string, params: any = {}) => {
    return apiClient.get(endpoint, { 
      params,
      headers: {
        'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/json, */*'
      }
    });
  },

  // Google Sheets APIs - Direct endpoints from main API
  getUsersExcel: (params: any = {}) => 
    apiService.callExcelEndpoint('/users/get-all-users-excel', params),
  getProductionFeeds: async (params: any = {}) => {
    try {
      // Always load ALL pages automatically - no user parameters needed
      const limit = 1000; // Optimal page size for performance
      let page = 1;
      let allData: any[] = [];
      
      console.log(`Auto-loading all Users Points data (${limit} records per page)...`);
      
      // Fetch all pages automatically
      while (true) {
        const requestParams = {
          limit,
          page,
          allowPagination: true
        };
        
        console.log(`Fetching page ${page}...`);
        
        const response = await apiClient.get('/users/production/feeds', { 
          params: requestParams,
          timeout: 120000 // 2 minutes per page
        });
        
        const responseData = response.data;
        
        // Check if response has docs array
        if (!responseData || !responseData.docs || !Array.isArray(responseData.docs)) {
          console.log('No docs array found - API may have returned all data in one response');
          // If no docs array on first page, return the whole response
          if (page === 1) {
            return response;
          } else {
            break; // No more data on subsequent pages
          }
        }
        
        const docs = responseData.docs;
        console.log(`Fetched page ${page}, records: ${docs.length}`);
        
        if (docs.length === 0) {
          console.log('No more data found, stopping pagination');
          break;
        }
        
        // Add this page's data to our collection
        allData = allData.concat(docs);
        console.log(`Total records fetched so far: ${allData.length}`);
        
        // If we got fewer records than the limit, we've reached the end
        if (docs.length < limit) {
          console.log('Received fewer records than limit, stopping pagination');
          break;
        }
        
        page++;
      }
      
      console.log(`Pagination complete. Total records: ${allData.length}`);
      
      // Return all collected data
      return {
        data: allData,
        status: 200,
        statusText: 'OK',
        headers: {},
        config: {}
      };
      
    } catch (error: any) {
      console.error('Error in getProductionFeeds:', error);
      throw error;
    }
  },
  
  getUsersWithAcceptedBy: (params: any = {}) =>
    apiClient.get('/users/usersWithAcceptedBy', { params }),
  getReferralSheet: (params: any = {}) =>
    apiClient.get('/users/data/sheet/referal', { params }),
  getReferredUsersSheet: (params: any = {}) =>
    apiClient.get('/users/data/sheet/referredUser', { params }),

  // Get user types for current supplier
  getUserTypes: async () => {
    const { supplierId } = getCredentials();
    
    // Check cache first
    const cacheKey = `userTypes_${supplierId}`;
    const cachedData = userTypesCache.get(cacheKey);
    if (cachedData && (Date.now() - cachedData.timestamp) < CACHE_DURATION) {
      console.log('Returning cached user types for supplier:', supplierId);
      return cachedData.data;
    }
    
    // Check if there's already a pending request for this supplier
    const requestKey = `getUserTypes_${supplierId}`;
    if (pendingRequests.has(requestKey)) {
      console.log('Joining existing request for user types:', supplierId);
      return await pendingRequests.get(requestKey);
    }
    
    // Create new request and store it in pending requests
    const requestPromise = (async () => {
      try {
        // Call the new API endpoint to get user types for the current supplier
        console.log('Fetching user types for supplier:', supplierId);
        const response = await apiClient.get(`/user-types/findUserTypesBySupplier/${supplierId}`);
        
        if (response.data && response.data.status === 201 && response.data.body && response.data.body.length > 0) {
          const supplierData = response.data.body[0]; // Get the first (and should be only) supplier data
          
          if (supplierData.userTypes && Array.isArray(supplierData.userTypes)) {
            // Extract user type names from the API response
            const userTypes = supplierData.userTypes
              .map((userType: any) => userType.name)
              .filter((name: string) => name && name.trim() !== '') // Remove empty names
              .sort(); // Sort alphabetically
            
            // Remove duplicates
            const uniqueUserTypes = Array.from(new Set(userTypes));
            
            const result = {
              data: uniqueUserTypes.length > 0 ? uniqueUserTypes : ['Admin', 'User'],
              totalUserTypes: supplierData.totalUserTypes || uniqueUserTypes.length
            };
            
            // Cache the result
            userTypesCache.set(cacheKey, { data: result, timestamp: Date.now() });
            
            return result;
          }
        }
        
        // If no valid response, return default
        const defaultResult = {
          data: ['Admin', 'User']
        };
        
        // Cache default result for a shorter time
        userTypesCache.set(cacheKey, { data: defaultResult, timestamp: Date.now() });
        
        return defaultResult;
      } catch (error) {
        console.error('Error loading user types from API:', error);
        // Fallback to default user types
        const defaultResult = {
          data: ['Admin', 'User']
        };
        
        // Cache error result for a shorter time  
        userTypesCache.set(cacheKey, { data: defaultResult, timestamp: Date.now() });
        
        return defaultResult;
      } finally {
        // Remove from pending requests when done
        pendingRequests.delete(requestKey);
      }
    })();
    
    // Store the promise in pending requests
    pendingRequests.set(requestKey, requestPromise);
    
    return await requestPromise;
  },

  // // Get all suppliers from user types data
  // getAllSuppliers: async () => {
  //   try {
  //     const userTypesData = require('../data/usertypes.json');
  //     const allSupplierData = userTypesData as SupplierUserTypes[];
      
  //     const suppliers = allSupplierData.map((supplierData: SupplierUserTypes) => ({
  //       id: supplierData.supplier.id.$oid,
  //       name: supplierData.supplier.name,
  //       totalUserTypes: supplierData.totalUserTypes
  //     }));
      
  //     return {
  //       data: suppliers.sort((a, b) => a.name.localeCompare(b.name))
  //     };
  //   } catch (error) {
  //     console.error('Error loading suppliers:', error);
  //     return {
  //       data: []
  //     };
  //   }
  // },

  // Get user types for a specific supplier by supplier ID
  getUserTypesBySupplier: async (supplierId: string) => {
    // Check cache first
    const cacheKey = `userTypesBySupplier_${supplierId}`;
    const cachedData = userTypesCache.get(cacheKey);
    if (cachedData && (Date.now() - cachedData.timestamp) < CACHE_DURATION) {
      console.log('Returning cached user types for supplier:', supplierId);
      return cachedData.data;
    }
    
    // Check if there's already a pending request for this supplier
    const requestKey = `getUserTypesBySupplier_${supplierId}`;
    if (pendingRequests.has(requestKey)) {
      console.log('Joining existing request for user types by supplier:', supplierId);
      return await pendingRequests.get(requestKey);
    }
    
    // Create new request and store it in pending requests
    const requestPromise = (async () => {
      try {
        // Call the new API endpoint to get user types for the specified supplier
        console.log('Fetching user types for supplier:', supplierId);
        const response = await apiClient.get(`/user-types/findUserTypesBySupplier/${supplierId}`);
        
        if (response.data && response.data.status === 201 && response.data.body && response.data.body.length > 0) {
          const supplierData = response.data.body[0]; // Get the first (and should be only) supplier data
          
          if (supplierData.userTypes && Array.isArray(supplierData.userTypes)) {
            const userTypes = supplierData.userTypes
              .map((userType: any) => userType.name)
              .filter((name: string) => name && name.trim() !== '')
              .sort();
            
            const uniqueUserTypes = Array.from(new Set(userTypes));
            
            // Get supplier name from the suppliers data (for backward compatibility)
            const supplierName = getSupplierNameByIdSync(supplierId) || 'Unknown Supplier';
            
            const result = {
              data: uniqueUserTypes,
              userTypesWithDetails: supplierData.userTypes, // Include full user type objects with userRole
              supplier: {
                id: supplierId,
                name: supplierName
              },
              totalUserTypes: supplierData.totalUserTypes || uniqueUserTypes.length
            };
            
            // Cache the result
            userTypesCache.set(cacheKey, { data: result, timestamp: Date.now() });
            
            return result;
          }
        }
        
        const defaultResult = {
          data: [],
          userTypesWithDetails: [],
          supplier: null,
          totalUserTypes: 0
        };
        
        // Cache default result
        userTypesCache.set(cacheKey, { data: defaultResult, timestamp: Date.now() });
        
        return defaultResult;
      } catch (error) {
        console.error('Error loading user types for supplier from API:', error);
        const errorResult = {
          data: [],
          userTypesWithDetails: [],
          supplier: null,
          totalUserTypes: 0
        };
        
        // Cache error result for a shorter time
        userTypesCache.set(cacheKey, { data: errorResult, timestamp: Date.now() });
        
        return errorResult;
      } finally {
        // Remove from pending requests when done
        pendingRequests.delete(requestKey);
      }
    })();
    
    // Store the promise in pending requests
    pendingRequests.set(requestKey, requestPromise);
    
    return await requestPromise;
  },

  // QR Code APIs
  getUserScanAnalysis: (params: any = {}) =>
    apiClient.get('/qr-code/userScanAnalisis', { params }),
  getScansDetailsPerUserType: async (params: any = {}) => {
    try {
      // Extract date parameters or use defaults
      const { startDate: startDateParam, endDate: endDateParam, ...otherParams } = params;
      
      // Helper function to add leading zero
      const addZero = (value: number | string): string => {
        const num = typeof value === 'string' ? parseInt(value, 10) : value;
        return num < 10 ? `0${num}` : `${num}`;
      };

      // Helper function to format date
      const formatDate = (date: Date): string => {
        const year = date.getFullYear();
        const month = addZero(date.getMonth() + 1);
        const day = addZero(date.getDate());
        return `${year}-${month}-${day}`;
      };

      // Set default dates if not provided
      let startDate: Date;
      let endDate: Date;
      
      if (startDateParam) {
        startDate = new Date(startDateParam);
      } else {
        // Default to start of current year
        startDate = new Date('2025-01-01');
      }

      if (endDateParam) {
        endDate = new Date(endDateParam);
      } else {
        // Default to current date
        endDate = new Date();
      }

      // Calculate date difference in days
      const timeDiff = Math.abs(endDate.getTime() - startDate.getTime());
      const dateDiffInDays = Math.ceil(timeDiff / (1000 * 3600 * 24));
      
      const allData: any[] = [];
      const incrementDays = 5; // Fetch 5 days at a time

      console.log(`Fetching scans data from ${formatDate(startDate)} to ${formatDate(endDate)} (${dateDiffInDays} days)`);

      if (dateDiffInDays < incrementDays) {
        // If the date difference is less than 5 days, fetch data for the entire range directly
        const response = await apiClient.get('/qr-code/scansDetailsPerUserType', { 
          params: {
            ...otherParams,
            startDate: formatDate(startDate),
            endDate: formatDate(endDate)
          }
        });
        
        if (response.data && Array.isArray(response.data)) {
          allData.push(...response.data);
        }
      } else {
        // Split the date range into chunks of incrementDays
        let currentStartDate = new Date(startDate);

        while (currentStartDate <= endDate) {
          const tempEndDate = new Date(currentStartDate);
          tempEndDate.setDate(tempEndDate.getDate() + incrementDays - 1);

          const actualEndDate = tempEndDate > endDate ? endDate : tempEndDate;
          
          console.log(`Fetching chunk: ${formatDate(currentStartDate)} to ${formatDate(actualEndDate)}`);
          
          try {
            const response = await apiClient.get('/qr-code/scansDetailsPerUserType', { 
              params: {
                ...otherParams,
                startDate: formatDate(currentStartDate),
                endDate: formatDate(actualEndDate)
              }
            });
            
            if (response.data && Array.isArray(response.data)) {
              allData.push(...response.data);
            }
          } catch (chunkError) {
            console.error(`Error fetching chunk ${formatDate(currentStartDate)} to ${formatDate(actualEndDate)}:`, chunkError);
            // Continue with next chunk instead of failing completely
          }

          if (tempEndDate > endDate) {
            break;
          } else {
            currentStartDate = new Date(tempEndDate);
            currentStartDate.setDate(currentStartDate.getDate() + 1);
          }
        }
      }

      console.log(`Total scans data fetched: ${allData.length} records`);

      // Return data in the same format as other API methods
      return {
        data: allData,
        status: 200,
        statusText: 'OK',
        headers: {},
        config: {}
      };
      
    } catch (error: any) {
      console.error('Error in getScansDetailsPerUserType:', error);
      throw error;
    }
  },
  getWarehouseScanInOutDetails: (params: any = {}) =>
    apiClient.get('/qr-code/warehouseScanInOutDetails', { params }),
  getDeactivatedIncentiveQR: (params: any = {}) =>
    apiService.callExcelEndpoint('/qr-code/deactivatedIncentiveQR', params),
  getConfigurationSheetCounterPerProduct: (params: any = {}) =>
    apiClient.get('/qr-code/configurationSheetCounterPerProduct', { params }),
  getWarehouseQuantityAggregation: (params: any = {}) =>
    apiClient.get('/qr-code/warehouseQuantityAggregation', { params }),
  getOrderScanoutFromWarehouse: (params: any = {}) =>
    apiClient.get('/qr-code/orderScanoutFromWarehouse', { params }),
  getScanPerProductDetails: (params: any = {}) =>
    apiClient.get('/qr-code/qrScanPerProductDetails', { params }),

  // Wallet APIs
  getWalletTransactionsExcel: (params: any = {}) =>
    apiService.callExcelEndpoint('/wallet-transactions/excel', params),
  getWalletTransactionsSheet: (params: any = {}) =>
    apiClient.get('/wallet-transactions/sheet', { params }),
  getUserBalanceSheet: (params: any = {}) =>
    apiClient.get('/wallet-transactions/user-balance-sheet', { params }),

  // Wallet APIs - additional methods
  getDeliverCash: (params: any = {}) =>
    apiClient.get('/wallet-transactions/deliverCash', { params }),
  getDeliverTransfer: (params: any = {}) =>
    apiClient.get('/wallet-transactions/deliverTransfer', { params }),
  getWelcomeBonus: (params: any = {}) => {
    // Add supplierId parameter from localStorage if not provided
    const { supplierId } = getCredentials();
    const defaultParams = { 
      supplierId: params.supplierId || supplierId || '',
      ...params 
    };
    return apiClient.get('/wallet-transactions/WelcomeBonusForSheet', { params: defaultParams });
  },
  getWalletTransactionSheet: (params: any = {}) =>
    apiClient.get('/wallet-transactions/WalletTransactionSheet', { params }),
  
  // Other APIs
  getBatchGenerationExcel: (params: any = {}) =>
    apiService.callExcelEndpoint('/batch-generation/excel', params),
  getInvoicePhotosSheet: (params: any = {}) => {
    const { supplierId } = getCredentials();
    return apiClient.get(`/invoice-photos/getInvoicesPhotosForSheet/${supplierId}`, { params: {} });
  },
  getInvoicePhotosExcel: (params: any = {}) =>
    apiService.callExcelEndpoint('/invoice-photos/excel', params),
  getPaymentLogs: (params: any = {}) =>
    apiClient.get('/payment-logs', { params }),
  getPaymentLogsExcel: (params: any = {}) =>
    apiService.callExcelEndpoint('/payment-logs/excel', params),
  getResetLogs: (params: any = {}) => {
    // Special handling for reset-logs endpoint - uses supplierName instead of supplier
    const { supplierName } = getCredentials();
    const resetLogsParams = {
      supplierName: supplierName,
      ...params
    };
    return apiClient.get('/reset-logs', { params: resetLogsParams });
  },
  getResetLogsExcel: (params: any = {}) =>
    apiService.callExcelEndpoint('/reset-logs/excel', params),
  getJoiningRequests: (params: any = {}) => {
    const { supplierId } = getCredentials();
    const requestParams = { ...params, supplierId };
    return apiClient.get('/joining-request/data/sheet/joining-request', { params: requestParams });
  },
  getReturnSystemSheet: (params: any = {}) =>
    apiClient.get('/return-system', { params }),
  getReturnSystemExcel: (params: any = {}) =>
    apiService.callExcelEndpoint('/return-system/excel', params),
  getDepositAmountOperations: (params: any = {}) =>
    apiClient.get('/wallet-transactions/multy-operations', { params }),
  submitDepositAmount: (params: any) =>
    apiClient.post('/wallet-transactions/multy-operations', params),
  getGiftsSheet: (params: any = {}) => {
    const { supplierName } = getCredentials();
    const giftsParams = {
      supplier: supplierName,
      ...params
    };
    return apiClient.get('/client-request/data/gift/sheet', { params: giftsParams });
  },

  // Get available endpoints for dashboard
  getEndpoints: async () => {
    // Mock response for now since this endpoint might not exist in main API
    return {
      data: {
        users: [
          { endpoint: 'Users', description: 'Users Data', category: 'Users', originalPath: '/users/usersWithAcceptedBy' },
          { endpoint: 'Users Types', description: 'Users Types Data', category: 'Users', originalPath: '/users/get-all-users-excel' },
          { endpoint: 'Users Points', description: 'Users Points Data', category: 'Users', originalPath: '/users/production/feeds' },
          // { endpoint: 'users/qara-feeds', description: 'Qara Feeds', category: 'Users', originalPath: '/users/qara/feeds' },
          // { endpoint: 'users/notification-feeds', description: 'Notification Feeds', category: 'Users', originalPath: '/users/notification/feeds' },
          // { endpoint: 'users/kz-report', description: 'KZ Users Report', category: 'Users', originalPath: '/users/kz/users-report' },
          { endpoint: 'Referral Users', description: 'Referral Users Data', category: 'Users', originalPath: '/users/data/sheet/referal' },
          { endpoint: 'Referred Users', description: 'Referred Users Data', category: 'Users', originalPath: '/users/data/sheet/referredUser' }
        ],
        'qr codes': [
          { endpoint: 'User Details Per Product Per Action', description: 'User Details Per Product Per Action Data', category: 'QR Codes', originalPath: '/qr-code/userScanAnalisis' },
          { endpoint: 'Scans per product per user type', description: 'Scan Details with User Product Data', category: 'QR Codes', originalPath: '/qr-code/scansDetailsPerUserType' },
          { endpoint: 'Warehouse Scan In/Out', description: 'Warehouse Scan In/Out Data', category: 'QR Codes', originalPath: '/qr-code/warehouseScanInOutDetails' },
          { endpoint: 'Deactivated Incentive QR', description: 'Deactivated Incentive QR Data', category: 'QR Codes', originalPath: '/qr-code/deactivatedIncentiveQR' },
          { endpoint: 'Configuration Result Per Product', description: 'Configuration Result Per Product Data', category: 'QR Codes', originalPath: '/qr-code/configurationSheetCounterPerProduct' },
          { endpoint: 'Order Scanout From Warehouse', description: 'Order Scanout From Warehouse Data', category: 'QR Codes', originalPath: '/qr-code/orderScanoutFromWarehouse' },
          { endpoint: 'Warehouse Quantity Aggregation', description: 'Warehouse Quantity Aggregation Data', category: 'Wallet', originalPath: '/qr-code/warehouseQuantityAggregation' },
          { endpoint: 'Scan Per Product details', description: 'Scan Per Product details Data', category: 'QR Codes', originalPath: '/qr-code/qrScanPerProductDetails' },
        ],
        wallet: [
          { endpoint: 'Wallet Transactions', description: 'Wallet Transactions Data', category: 'Wallet', originalPath: '/wallet-transactions/WalletTransactionSheet' },
          { endpoint: 'Deliver Cash', description: 'Deliver Cash Data', category: 'Wallet', originalPath: '/wallet-transactions/deliverCash' },
          { endpoint: 'Deliver Transfer', description: 'Deliver Transfer Data', category: 'Wallet', originalPath: '/wallet-transactions/deliverTransfer' },
          { endpoint: 'Welcome Bonus', description: 'Welcome Bonus Data', category: 'Wallet', originalPath: '/wallet-transactions/WelcomeBonusForSheet' },
        ],
        Paymob: [
          { endpoint: 'Paymob', description: 'Payment Logs Data', category: 'Paymob', originalPath: '/payment-logs' },
        ],
        Invoice: [
          { endpoint: 'invoice-photos', description: 'Invoice Photos Data', category: 'Invoice', originalPath: '/invoice-photos/getInvoicesPhotosForSheet' },
        ],
        'Transaction Logs': [
          { endpoint: 'reset-logs', description: 'Transaction Data Logs', category: 'Transaction Logs', originalPath: '/reset-logs' },
        ],
        'Joining Requests': [
          { endpoint: 'joining-requests', description: 'Joining Requests Data', category: 'Joining Requests', originalPath: '/joining-request/data/sheet/joining-request' },
        ],
        'return system': [
          { endpoint: 'Return System', description: 'Return System Data', category: 'Return System', originalPath: '/return-system/returnSystemSheet' },
          { endpoint: 'Incentive', description: 'Incentive System Data', category: 'Return System', originalPath: '/return-system/returnSystemSheet' },

        ],
        'Deposit Amount': [
          { endpoint: 'Deposit Amount', description: 'Enter Deposit Amount Operations', category: 'Deposit Amount', originalPath: '/wallet-transactions/multy-operations' },
          { endpoint: 'Deposit Amount Log', description: 'Deposit Amount Operations Data Log', category: 'Deposit Amount', originalPath: '/wallet-transactions/multy-operations' }
        ],
        'Gifts': [
          { endpoint: 'Gifts', description: 'Gifts Data log', category: 'Gifts', originalPath: '/client-request/data/gift/sheet' },
        ]

      }
    };
  },

  // Dynamic API call for any endpoint
  callDynamicAPI: (endpoint: string, params: any) => {
    // Extract the path from the endpoint
    const path = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
    
    // Check if this is an Excel endpoint
    if (path.includes('/excel')) {
      return apiService.callExcelEndpoint(path, params);
    }
    
    return apiClient.get(path, { params });
  }
};
